// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from '@stencil/core'
import {
  Component,
  Element,
  Event,
  h,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core'

import type { Attributes, QdsFocusEventDetail } from '../../helpers'
import { inheritAriaAttributes, pickFocusEventAttributes } from '../../helpers'
import type { Size } from '../shared'

export type Destination = 'navigation'
export type ListAction = 'multiselect' | 'reorder'
export type Divider = 'hide' | 'indent'

/**
 *
 *
 * @see https://quartz.se.com/build/components/list-item
 */
@Component({
  tag: 'qds-list-item',
  shadow: { delegatesFocus: true },
  styleUrl: 'list-item.css',
})
export class ListItem implements ComponentInterface {
  /**
   * The destination action that the list item denotes through its rightmost icon.
   */
  @Prop() public readonly destination?: Destination

  /**
   * Prevents the list item from being interacted with: it cannot be selected
   * or focused.
   */
  @Prop() public readonly disabled: boolean = false

  /**
   * Control how to display the bottom divider line.
   *
   * By default, the divider is visible and uses the full width of the list item.
   *
   * Use the `hide` value to hide the divider completely (useful with multiselect).
   *
   * Use the `indent` value to align the left end of the divider to the icon/text (useful on mobile).
   *
   * **Note**: the divider is automatically hidden on the last list item in the parent container.
   */
  @Prop() public readonly divider?: Divider

  /**
   * The name of the icon to render.
   *
   * Available names depend on the icon library being used.
   */
  @Prop() public readonly iconName?: string

  /**
   * The name of a registered icon library.
   */
  @Prop() public readonly iconLibrary: string = 'default'

  /**
   * Text to display for the kicker.
   */
  @Prop() public readonly kicker?: string

  /**
   * The type of list-level action that the list item supports
   */
  @Prop() public readonly listAction?: ListAction

  /**
   * The list items's size.
   */
  @Prop() public readonly size?: Size = 'standard'

  /**
   * Whether or not the list item is checked when `list-action="multiselect"`
   */
  @Prop({ mutable: true }) public checked?: boolean

  /**
   * Whether or not the list item is selected.
   */
  @Prop({ mutable: true }) public selected?: boolean

  /**
   * Sets the list item to an indeterminate state, only when `list-action="indeterminate"`.
   * This is usually applied to items that represent a "select all/none" behavior when associated
   * items have a mix of selected and unselected states.
   *
   * @webnative
   */
  @Prop({ mutable: true }) public indeterminate?: boolean

  /**
   * The primary text of the list item.
   */
  @Prop() public readonly text!: string

  /**
   * Text to display below the primary text.
   */
  @Prop() public readonly subtitle?: string

  /**
   * Emitted when the list item loses focus.
   */
  @Event({ eventName: 'qdsBlur', bubbles: false, cancelable: false })
  private readonly blurEmitter!: EventEmitter<QdsFocusEventDetail>

  /**
   * Emitted when a change to the checkbox's state is committed by the user.
   */
  @Event({ eventName: 'qdsChange', cancelable: false })
  private readonly changeEmitter!: EventEmitter<void>

  /**
   * Emitted when the list item gains focus.
   */
  @Event({ eventName: 'qdsFocus', bubbles: false, cancelable: false })
  private readonly focusEmitter!: EventEmitter<QdsFocusEventDetail>

  @Element() private readonly host!: HTMLElement

  @State() private tabIndex?: number

  private inheritedAttributes: Attributes = {}

  get #computedDestination(): Destination | undefined {
    switch (this.destination) {
      case 'navigation': {
        return this.destination
      }
      default: {
        return undefined
      }
    }
  }

  get #computedDivider(): Divider | undefined {
    switch (this.divider) {
      case 'hide':
      case 'indent': {
        return this.divider
      }
      default: {
        return undefined
      }
    }
  }

  get #computedIndeterminate(): boolean {
    return this.indeterminate ?? false
  }

  get #computedListAction(): ListAction | undefined {
    switch (this.listAction) {
      case 'reorder':
      case 'multiselect': {
        return this.listAction
      }
      default: {
        return undefined
      }
    }
  }

  get #computedSelected(): boolean {
    return this.selected ?? false
  }

  get #computedChecked(): boolean {
    return this.checked ?? false
  }

  get #computedSize(): Size {
    switch (this.size) {
      case 'standard':
      case 'small':
      case 'large': {
        return this.size
      }
      default: {
        return 'standard'
      }
    }
  }

  @Listen('click')
  protected onClick(event: MouseEvent): void {
    if (this.disabled) {
      event.stopImmediatePropagation()
      return
    }

    // Always prevent default behavior to avoid the internal `<input>`
    // to toggle `checked` state inappropriately.
    //
    // Note that this will need adjustments once we add the `item-action` slot.
    event.preventDefault()

    if (this.listAction === 'multiselect') {
      this.checked = this.#computedIndeterminate ? true : !this.#computedChecked
      this.changeEmitter.emit()
      this.indeterminate = false
    }
  }

  @Watch('tabindex')
  protected tabindexChanged(newValue: string): void {
    const parsedValue = Number.parseInt(newValue, 10)
    this.tabIndex =
      parsedValue === 0 || Number.isNaN(parsedValue) ? undefined : parsedValue
  }

  public componentWillLoad(): void {
    this.inheritedAttributes = inheritAriaAttributes(this.host)
    this.tabindexChanged(this.host.getAttribute('tabindex') ?? '0')
  }

  public render() {
    return (
      /* eslint-disable jsx-a11y/label-has-associated-control */
      // eslint-disable-next-line jsx-a11y/no-redundant-roles
      <li
        class={{
          'qds-list-item': true,
          'qds-disabled': this.disabled,
          'qds-divider': this.#computedDivider === undefined,
          'qds-multiselect': this.#computedListAction === 'multiselect',
          'qds-selected': this.#computedSelected,
          'qds-checked': this.#computedChecked,
        }}
        data-size={this.#computedSize}
        role="listitem" // Required because it can't be a direct child of a list element (<ul>, <ol>, or <menu>).
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...this.inheritedAttributes}
      >
        {this.#computedListAction === 'multiselect' && (
          <qds-checkbox
            checked={this.checked}
            class="qds-list-action"
            indeterminate={this.indeterminate}
            size={this.size}
            tabIndex={-1}
            text={this.text}
            checkboxOnly
          />
        )}
        {this.#computedListAction === 'reorder' && (
          <qds-icon
            aria-hidden="true"
            class="qds-icon qds-list-action"
            library="core"
            name={this.#computedListAction}
          />
        )}

        <div
          class={{
            'qds-indented-content': true,
            'qds-divider': this.#computedDivider === 'indent',
          }}
        >
          {this.iconName !== undefined && this.iconName !== '' && (
            <qds-icon
              aria-hidden="true"
              class="qds-icon qds-main-icon"
              library={this.iconLibrary}
              name={this.iconName}
            />
          )}

          <label class="qds-texts">
            <input
              checked={this.selected}
              class="qds-input"
              disabled={this.disabled}
              indeterminate={this.indeterminate}
              onBlur={this.#onBlur}
              onFocus={this.#onFocus}
              tabIndex={this.disabled ? undefined : this.tabIndex}
              type="checkbox"
            />
            {this.kicker !== undefined && this.kicker !== '' && (
              <div class="qds-kicker">{this.kicker}</div>
            )}
            <div class="qds-title">{this.text}</div>
            {this.subtitle !== undefined && this.subtitle !== '' && (
              <div class="qds-subtitle">{this.subtitle}</div>
            )}
          </label>
          {this.#computedDestination !== undefined && (
            <qds-icon
              aria-hidden="true"
              class="qds-icon qds-destination"
              library="core"
              name={this.#computedDestination}
            />
          )}
        </div>
      </li>
      /* eslint-enable jsx-a11y/label-has-associated-control */
    )
  }

  readonly #onBlur = (event: FocusEvent): void => {
    this.blurEmitter.emit(pickFocusEventAttributes(event))
  }

  readonly #onFocus = (event: FocusEvent): void => {
    this.focusEmitter.emit(pickFocusEventAttributes(event))
  }
}
